// src/components/AllCataloguesSection.js

import React, { useState, useEffect } from 'react';
import './AllCataloguesSection.css'; // Your existing CSS file
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';
import Papa from 'papaparse'; // Import PapaParse

// --- Hardcoded data has been removed ---
// const cataloguesData = [ ... ];

// Validation schema remains the same
const validationSchema = yup.object().shape({
  from_name: yup.string().required('Your name is required.'),
  from_email: yup.string().email('Please enter a valid email.').required('Your email is required.'),
  from_phone: yup.string().required('A valid phone number is required.'),
  message: yup.string().required('Please tell us about your project or inquiry.'),
  recaptcha: yup.string().required('Please verify you are not a robot.'),
});

// POP-UP FORM component remains completely unchanged
const DownloadFormPopup = ({ onClose, onSuccessfulSubmit, catalogueTitle }) => {
  // ... (Your existing popup code is unchanged)
  const SCRIPT_URL = "https://script.google.com/macros/s/AKfycbzJYFBgsdhes41k5I8mjvTBDCnqIATz_P_EGlAdx-8wRyFY5G3GSZrguR9ZcUNf86Mp/exec";
  const RECAPTCHA_SITE_KEY = "6LdG4tcrAAAAACrpa9DG-_S4Jv4SFph_gFP4N8rj";
  const [statusMessage, setStatusMessage] = useState('');
  const { register, handleSubmit, reset, control, formState: { errors, isSubmitting } } = useForm({ resolver: yupResolver(validationSchema) });
  const onSubmit = async (data) => {
    setStatusMessage('Submitting...');
    const payload = {
      name: data.from_name, email: data.from_email, phone: data.from_phone, message: data.message,
      recaptchaToken: data.recaptcha, catalogueName: catalogueTitle || 'Unknown Catalogue'
    };
    try {
      const response = await fetch(SCRIPT_URL, {
        method: 'POST', headers: { 'Content-Type': 'text/plain;charset=utf-8' }, body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (result.result === 'success') {
        setStatusMessage('Success! Download starting...');
        reset();
        onSuccessfulSubmit();
        setTimeout(() => onClose(), 2000);
      } else { throw new Error(result.message || 'Verification failed.'); }
    } catch (error) { setStatusMessage(`Error: ${error.message}`); }
  };
  useEffect(() => {
    const handleKeyDown = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-content" onClick={(e) => e.stopPropagation()}>
        <button className="popup-close-btn" onClick={onClose}>&times;</button>
        <div className="popup-header"><h3>Download: {catalogueTitle}</h3><p>Please provide your details to begin.</p></div>
        <form className="popup-form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="form-group"><input {...register('from_name')} placeholder="Full Name" />{errors.from_name && <span className="error-message">{errors.from_name.message}</span>}</div>
          <div className="form-group"><input {...register('from_email')} placeholder="Email Address" />{errors.from_email && <span className="error-message">{errors.from_email.message}</span>}</div>
          <div className="form-group"><input {...register('from_phone')} placeholder="Phone Number" />{errors.from_phone && <span className="error-message">{errors.from_phone.message}</span>}</div>
          <div className="form-group"><textarea {...register('message')} rows="4" placeholder="Message / Project Details"></textarea>{errors.message && <span className="error-message">{errors.message.message}</span>}</div>
          <div className="form-group"><Controller name="recaptcha" control={control} render={({ field }) => (<ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} onChange={(token) => field.onChange(token)} />)} />{errors.recaptcha && <span className="error-message">{errors.recaptcha.message}</span>}</div>
          <button type="submit" className="popup-submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Processing...' : 'Submit & Download'}</button>
          {statusMessage && <p className="popup-status-message">{statusMessage}</p>}
        </form>
      </div>
    </div>
  );
};


// ==========================================================
//      REDESIGNED: MAIN SECTION COMPONENT
// ==========================================================
const AllCataloguesSection = () => {
  // --- NEW: State for catalogues, loading, and errors ---
  const [catalogueList, setCatalogueList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  
  // --- NEW: Data Fetching Logic ---
  useEffect(() => {
    // PASTE YOUR PUBLISHED GOOGLE SHEET URL HERE
    const GOOGLE_SHEET_URL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vQd1CoMIcSXJsHiuFkAmaX6q1a17rP2ux4OoLuUFUdgKKTPfKWPVnxoRJ3fy31C1eRpWLmd8vI3SdFl/pub?output=csv";

    Papa.parse(GOOGLE_SHEET_URL, {
        download: true,
        header: true,
        complete: (results) => {
            const validData = results.data.filter(row => row.id && row.id.trim() !== "");
            setCatalogueList(validData);
            setIsLoading(false);
        },
        error: (err) => {
            console.error("Error fetching catalogue data:", err);
            setError("Could not load catalogues. Please try again later.");
            setIsLoading(false);
        },
    });
  }, []); // Empty dependency array means this runs once on mount

  
  // Get the currently selected catalogue safely
  const activeCatalogue = catalogueList.length > 0 ? catalogueList[activeIndex] : null;

  const handleOpenPopup = () => {
    if (activeCatalogue) setIsPopupVisible(true);
  };

  const handleClosePopup = () => setIsPopupVisible(false);
  
  const handleDownloadTrigger = () => {
    if (!activeCatalogue || !activeCatalogue.pdfUrl) return;
    const link = document.createElement('a');
    link.href = activeCatalogue.pdfUrl;
    link.setAttribute('download', activeCatalogue.pdfUrl.split('/').pop());
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  useEffect(() => {
    document.body.style.overflow = isPopupVisible ? 'hidden' : 'auto';
  }, [isPopupVisible]);
  
  // --- NEW: Loading and Error State Handling ---
  if (isLoading) {
    return <section className="all-catalogues-section"><p className="catalogue-status-message">Loading Catalogues...</p></section>;
  }

  if (error) {
    return <section className="all-catalogues-section"><p className="catalogue-status-message error">{error}</p></section>;
  }

  if (catalogueList.length === 0) {
      return <section className="all-catalogues-section"><p className="catalogue-status-message">No catalogues are available at this time.</p></section>;
  }


  return (
    <>
      <section className="all-catalogues-section">
        <div className="catalogue-header">
          <h1>Our Catalogues</h1>
          <p>Browse our curated collections of innovative lighting solutions.</p>
        </div>
        
        <div className="split-view-container">
          <div className="catalogue-list-panel">
            <ul>
              {catalogueList.map((catalogue, index) => (
                <li
                  key={catalogue.id}
                  className={`list-item ${index === activeIndex ? 'active' : ''}`}
                  onClick={() => setActiveIndex(index)}
                >
                  <span className="item-number">{`0${index + 1}`}</span>
                  <span className="item-title">{catalogue.title}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="catalogue-display-panel">
            {activeCatalogue && (
              <div key={activeIndex} className="display-content-wrapper">
                <div
                  className="display-background"
                  style={{ backgroundImage: `url(${activeCatalogue.coverImageUrl})` }}
                />
                <div className="display-content">
                  <h3 className="display-title">{activeCatalogue.title}</h3>
                  <p className="display-description">{activeCatalogue.description}</p>
                  <button className="display-download-btn" onClick={handleOpenPopup}>
                    Download Now
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

      </section>
      
      {isPopupVisible && (
        <DownloadFormPopup 
          onClose={handleClosePopup} 
          onSuccessfulSubmit={handleDownloadTrigger}
          catalogueTitle={activeCatalogue?.title}
        />
      )}
    </>
  );
};

export default AllCataloguesSection;